<template>
  <div
    :class="[
      $style.card,
      {
        [$style.cardPlaceholder]: isPlaceholder,
        [$style.cardFooter]: isCatalogFooterVariant,
      },
    ]"
  >
    <div v-if="isPlaceholder" :class="$style.spinner">
      <aw-spinner />
      <span v-text="$awt('aw.global.loading')" />
    </div>
    <nuxt-link
      :to="catalog.flipbookUrl || ''"
      :class="$style.cardOverlay"
      target="_blank"
      aria-hidden="true"
      tabindex="-1"
      v-on="clickEventHandlers"
    />
    <div :class="[$style.cardImageWrapper, { [$style.cardImageWrapperFooter]: isCatalogFooterVariant }]">
      <lv-image
        :sources="imgSources"
        alt=""
        loading="lazy"
        :class="$style.cardImage"
        :sizing="isCatalogFooterVariant ? 'contain' : ''"
      />
    </div>
    <div :class="$style.cardContent">
      <nuxt-link
        :to="catalog.flipbookUrl"
        :class="$style.cardContentTitle"
        target="_blank"
        v-on="clickEventHandlers"
      >
        <h3 :title="catalog.title">
          {{ catalog.title || '\xA0' }}
        </h3>
      </nuxt-link>
      <p :class="$style.cardContentDescription">
        {{ catalogAvailabilityText }}
      </p>
      <lv-button
        styling="small-secondary-dark"
        shrink
        :class="$style.cardContentButton"
        :to="catalog.flipbookUrl || ''"
        target="_blank"
        v-on="clickEventHandlers"
      >
        {{ $awt('aw.cms.catalog.card.button_text') }}
      </lv-button>
    </div>
  </div>
</template>

<script setup>
  import { computed, inject } from 'vue';
  import { useNuxtApp } from 'nuxt/app';
  import { webpFallback } from '~~/common/utils';
  import { genBannerInteractionClickEventHandlers } from '~~/common/utils/bannerInteractionAnalytics';
  import { AnalyticsBannerInteraction, EMPTY_ANALYTICS_BANNER_INTERACTION } from '~~/common/utils/AnalyticsBannerInteraction';
  import { LvButton, LvImage } from '~~/common/components/loginet-vue-shop/index';
  import AwSpinner from '~~/common/components/Common/AwSpinner';

  const props = defineProps({
    catalog: {
      type: Object,
      required: true,
    },
    analyticsBannerInteraction: {
      type: [Object, Symbol],
      default: null,
      validator: !AnalyticsBannerInteraction || ((val) => {
        return (val === EMPTY_ANALYTICS_BANNER_INTERACTION) || AnalyticsBannerInteraction.isValid(val);
      }),
    },
    analyticsMeasurable: {
      type: Boolean,
      default: false,
    },
  });

  const isCatalogFooterVariant = inject('catalogFooterVariant', false);

  const { $date } = useNuxtApp();

  const isPlaceholder = computed(() => props.catalog?.isPlaceholder);

  const catalogAvailabilityText = computed(() => {
    if (!props.catalog || isPlaceholder.value) {
      return '\xA0';
    }
    const {
      availabilityFromDate,
      availabilityToDate,
    } = props.catalog;
    return `${toDate(availabilityFromDate)} - ${toDate(availabilityToDate)}`;
  });

  const imgSources = computed(() => webpFallback([{ url: props.catalog.cover || '' }]));

  const clickEventHandlers = computed(() => genBannerInteractionClickEventHandlers({
    analyticsBannerInteraction: props.analyticsBannerInteraction,
    analyticsMeasurable: props.analyticsMeasurable,
  }));

  function toDate (date) {
    return $date(date, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }, { removeSpaces: true });
  }
</script>

<style module lang="scss" rel="stylesheet/scss">
.card {
  &Overlay {
    position: absolute;
    inset: 0;
    z-index: 1;
    border-radius: inherit;
  }

  &Placeholder {
    position: relative;
    opacity: 0.5;
    background: $color-white;

    > .spinner {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }

    > :not(.spinner) {
      visibility: hidden;
    }
  }

  position: relative;
  display: grid;

  justify-content: space-between;
  min-width: 160px;
  border: 1px solid $color-border;
  border-radius: 16px;
  background: $color-background-2;
  grid-template-columns: 1fr;
  justify-items: center;

  &Footer {
    width: 260px;
  }

  &Image {
    height: 144px;

    &Wrapper {
      display: flex;
      overflow: hidden;
      align-items: center;
      justify-content: center;
      width: 100%;
      border-radius: 16px;
      background: white;

      &Footer {
        width: 258px;
        height: 390px;

        .cardImage {
          height: 100%;
        }
      }
    }
  }

  &Content {
    display: grid;
    width: 100%;
    padding: 24px;
    grid-template-columns: 1fr;
    grid-template-rows: repeat(3, max-content);
    gap: 8px;

    &Title {
      font-size: 18px;
      font-weight: $heading-3-font-weight-desktop-version2;
      line-height: 24px;
      position: relative;
      z-index: 2;

      display: -webkit-inline-box;
      overflow: hidden;
      min-height: 48px;
      text-overflow: ellipsis;
      word-break: break-word;
      hyphens: auto;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
    }

    &Description {
      font-size: 12px;
      font-weight: $font-weight-regular-v2;
      line-height: 16px;
      margin-bottom: 8px;
      color: $color-text-secondary;
    }

    &Button {
      position: relative;
      z-index: 2;
      max-width: max-content;
    }
  }
}
</style>
